/**
 * API Error Handling
 * 
 * Custom error class and utilities for API errors.
 * Surfaces connectivity failures to the user via `butterToast` (@butter/ui/patterns).
 */

import { butterToast } from '@butter/ui/patterns';

import { ERROR_CODES, type ErrorCode } from '../constants';
import type { ApiErrorResult, RequestOptions } from '../types';

// ============================================================================
// ApiError Class
// ============================================================================

/**
 * Custom API Error class
 * 
 * Preserves error details for debugging and user messaging.
 * Extends native Error with additional metadata.
 * 
 * @example
 * ```typescript
 * throw new ApiError(
 *   'User not found',
 *   ERROR_CODES.NOT_FOUND,
 *   {
 *     status: 404,
 *     userMessage: 'We couldn't find that user. Please check the ID.',
 *     details: { userId: 123 }
 *   }
 * );
 * ```
 */
export class ApiError extends Error {
  /**
   * Machine-readable error code
   * Used for error handling logic
   */
  public readonly code: ErrorCode;
  
  /**
   * HTTP status code (if available)
   * May be undefined for network errors
   */
  public readonly status?: number;
  
  /**
   * Additional error details
   * For debugging and logging
   */
  public readonly details?: unknown;
  
  /**
   * Field name (for validation errors)
   * Indicates which field caused the error
   */
  public readonly field?: string;
  
  /**
   * User-friendly error message
   * Safe to display to end users
   */
  public readonly userMessage: string;

  constructor(
    message: string,
    code: ErrorCode = ERROR_CODES.INTERNAL_ERROR,
    options?: {
      status?: number;
      details?: unknown;
      field?: string;
      userMessage?: string;
    }
  ) {
    super(message);
    this.name = 'ApiError';
    this.code = code;
    this.status = options?.status;
    this.details = options?.details;
    this.field = options?.field;
    this.userMessage = options?.userMessage || message;
    
    // Maintain proper stack trace for debugging
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, ApiError);
    }
  }
}

// ============================================================================
// Error Message Utilities
// ============================================================================

/**
 * Extract user-friendly error message from any error type
 * 
 * Handles:
 * - ApiError instances
 * - Native Error instances
 * - ApiResult error responses
 * - Unknown error types
 * 
 * @param error - Error to extract message from
 * @returns User-friendly error message
 * 
 * @example
 * ```typescript
 * try {
 *   await apiClient.get('/users/1');
 * } catch (error) {
 *   const message = getUserErrorMessage(error);
 *   console.error(message); // "User not found"
 * }
 * ```
 */
export function getUserErrorMessage(error: unknown): string {
  // ApiError with custom user message
  if (error instanceof ApiError) {
    return error.userMessage;
  }
  
  // Native Error
  if (error instanceof Error) {
    return error.message;
  }
  
  // ApiResult error response
  if (error && typeof error === 'object' && 'success' in error && !error.success) {
    const apiError = error as ApiErrorResult;
    return apiError.message;
  }
  
  // Unknown error type
  return 'An unexpected error occurred';
}

/**
 * Extract error code from any error type
 * 
 * @param error - Error to extract code from
 * @returns Error code or INTERNAL_ERROR if not found
 * 
 * @example
 * ```typescript
 * const code = getErrorCode(error);
 * if (code === ERROR_CODES.UNAUTHORIZED) {
 *   // Redirect to login
 * }
 * ```
 */
export function getErrorCode(error: unknown): ErrorCode {
  if (error instanceof ApiError) {
    return error.code;
  }
  
  // ApiResult error response with code
  if (error && typeof error === 'object' && 'success' in error && !error.success) {
    const apiError = error as ApiErrorResult;
    if (apiError.error?.code) {
      return apiError.error.code as ErrorCode;
    }
  }
  
  return ERROR_CODES.INTERNAL_ERROR;
}

// ============================================================================
// Toast Integration
// ============================================================================

/**
 * Determine if error should be shown as toast
 * 
 * **IMPORTANT:** Only network errors are auto-shown.
 * Validation errors must be handled by components.
 * 
 * Auto-show rules:
 * - ✅ Network errors (NETWORK_ERROR, TIMEOUT_ERROR)
 * - ❌ Validation errors (400, VALIDATION_ERROR)
 * - ❌ Unauthorized (401) - redirects to login instead
 * - ⚙️  Respects showErrorToast option
 * 
 * @param error - Error to check
 * @param options - Request options
 * @returns true if toast should be shown
 * 
 * @example
 * ```typescript
 * if (shouldShowErrorToast(error, options)) {
 *   toast.error(getUserErrorMessage(error));
 * }
 * ```
 */
export function shouldShowErrorToast(
  error: unknown,
  options?: RequestOptions
): boolean {
  // Respect explicit option
  if (options?.showErrorToast === false) {
    return false;
  }
  
  // Don't show toast for 401 (will redirect to login)
  if (error instanceof ApiError && error.status === 401) {
    return false;
  }
  
  // Don't show toast for validation errors (400)
  // Components handle these with form validation
  if (error instanceof ApiError && error.status === 400) {
    return false;
  }
  
  // Check if it's a network error
  const isNetworkError = 
    (error instanceof ApiError && error.code === ERROR_CODES.NETWORK_ERROR) ||
    (error instanceof ApiError && error.code === ERROR_CODES.TIMEOUT_ERROR) ||
    (error instanceof TypeError); // fetch() network errors are TypeError
  
  // Only show toast for network errors by default
  return isNetworkError;
}

/**
 * Stable id for the connectivity toast.
 *
 * This fires from the apiClient, so every in-flight request that fails sees it —
 * a booking page fanning out club, bays, availability and terms would otherwise
 * stack four identical "check your connection" toasts. A fixed id makes sonner
 * update the existing toast in place, so losing the network is always one toast.
 * Matches `NETWORK_ERROR_TOAST_ID` in mobile-app's apiClient.
 */
const NETWORK_ERROR_TOAST_ID = 'api-network-error';

/**
 * Show error toast using toast system
 *
 * Fires only for network/timeout failures (see {@link shouldShowErrorToast}), so
 * this is a connectivity notice rather than a general error channel — and only
 * for reads.
 *
 * **Why reads only.** Mutations almost always have their own `onError`/`catch`
 * that toasts, so raising here too gave one offline submit two stacked toasts.
 * Worse, the second read badly: call sites toast `error.message`, which comes
 * from `result.message`, which `apiClient` fills from the raw fetch error —
 * "Failed to fetch". So the user got a good message followed by a raw one.
 * Queries are the opposite: their consumers render inline or ignore the error
 * entirely, so without this the failure is invisible.
 *
 * The raw-`message` half of that is platform-wide and deliberately not fixed
 * here — it is step 0 of /docs/refactors/internalapi-envelope-rollout.md, which
 * also removes the need for call sites to toast off a bare `Error` at all.
 *
 * @param error - Error to show
 * @param method - Uppercased HTTP verb of the request that failed
 * @param options - Request options
 *
 * @example
 * ```typescript
 * try {
 *   await fetch('/api/users');
 * } catch (error) {
 *   showErrorToast(error, 'GET', options);
 *   // Network errors on reads shown as toast automatically
 *   // Validation errors must be handled by component
 * }
 * ```
 */
export function showErrorToast(
  error: unknown,
  method: string,
  options?: RequestOptions
): void {
  if (method !== 'GET') {
    return;
  }

  if (!shouldShowErrorToast(error, options)) {
    return;
  }

  butterToast.error(getUserErrorMessage(error), null, { id: NETWORK_ERROR_TOAST_ID });
}

// ============================================================================
// Error Type Guards
// ============================================================================

/**
 * Check if error is an ApiError
 * 
 * @param error - Error to check
 * @returns true if error is ApiError instance
 */
export function isApiError(error: unknown): error is ApiError {
  return error instanceof ApiError;
}

/**
 * Check if error is a network error
 * 
 * @param error - Error to check
 * @returns true if error is a network error
 */
export function isNetworkError(error: unknown): boolean {
  if (error instanceof ApiError) {
    return error.code === ERROR_CODES.NETWORK_ERROR;
  }
  
  // fetch() network errors are TypeError
  return error instanceof TypeError;
}

/**
 * Check if error is a server error (5xx)
 * 
 * @param error - Error to check
 * @returns true if error is a server error (500-599)
 */
export function isServerError(error: unknown): boolean {
  if (error instanceof ApiError && error.status) {
    return error.status >= 500 && error.status < 600;
  }
  
  if (error instanceof Error) {
    const message = error.message.toLowerCase();
    return message.includes('500') ||
           message.includes('502') ||
           message.includes('503') ||
           message.includes('504');
  }
  
  return false;
}

/**
 * Check if error is a connectivity error (network or server)
 * 
 * Useful for determining if retry/refresh might help.
 * 
 * @param error - Error to check
 * @returns true if error is network or server-related
 */
export function isConnectivityError(error: unknown): boolean {
  return isNetworkError(error) || isServerError(error);
}

/**
 * Get user-friendly error title based on error type
 * 
 * @param error - Error to get title for
 * @returns User-friendly error title
 */
export function getErrorTitle(error: unknown): string {
  if (isNetworkError(error)) {
    return 'Unable to Connect';
  }
  
  if (isServerError(error)) {
    return 'Server Error';
  }
  
  if (error instanceof ApiError) {
    switch (error.status) {
      case 401:
        return 'Authentication Required';
      case 403:
        return 'Access Denied';
      case 404:
        return 'Not Found';
      case 409:
        return 'Conflict';
      case 422:
        return 'Validation Error';
      default:
        return 'Error';
    }
  }
  
  return 'Error';
}

/**
 * Get detailed user message with recovery suggestions
 * 
 * @param error - Error to get message for
 * @returns Detailed user-friendly message with recovery suggestions
 */
export function getDetailedErrorMessage(error: unknown): string {
  if (isNetworkError(error)) {
    return 'We\'re having trouble connecting to our servers. Please check your internet connection and try refreshing the page.';
  }
  
  if (isServerError(error)) {
    return 'Our servers are experiencing issues. Please try again in a few moments. If the problem persists, contact support.';
  }
  
  return getUserErrorMessage(error);
}

/**
 * Check if error is a timeout error
 * 
 * @param error - Error to check
 * @returns true if error is a timeout error
 */
export function isTimeoutError(error: unknown): boolean {
  if (error instanceof ApiError) {
    return error.code === ERROR_CODES.TIMEOUT_ERROR;
  }
  
  // AbortController timeout errors
  if (error instanceof DOMException && error.name === 'AbortError') {
    return true;
  }
  
  return false;
}

/**
 * Check if error is a validation error
 * 
 * @param error - Error to check
 * @returns true if error is a validation error
 */
export function isValidationError(error: unknown): boolean {
  if (error instanceof ApiError) {
    return error.code === ERROR_CODES.VALIDATION_ERROR || error.status === 400;
  }
  
  return false;
}

/**
 * Check if error is an authorization error
 * 
 * @param error - Error to check
 * @returns true if error is 401 or 403
 */
export function isAuthError(error: unknown): boolean {
  if (error instanceof ApiError) {
    return error.status === 401 || error.status === 403;
  }
  
  return false;
}
